import { UploadOutlined, CopyOutlined, CheckOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Spin, Upload, Table, Typography, Divider, message } from "antd";
import { UploadProps } from "antd/es/upload";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import { CLIENT_KEYS, useImportClients } from "~/services/client";
import { useClientStore } from "./clients.store";

const { Text, Title } = Typography;

const CSV_COLUMNS = [
  { key: "COMPANY_NAME", required: true, description: "Company or client name", example: "Gagosian Gallery" },
  { key: "FIRST_NAME", required: true, description: "Contact first name", example: "John" },
  { key: "LAST_NAME", required: true, description: "Contact last name", example: "Smith" },
  { key: "EMAIL", required: "conditional", description: "Email address — required if no CONTACT_PHONE", example: "john@gagosian.com" },
  { key: "CONTACT_PHONE", required: "conditional", description: "Primary phone — required if no EMAIL", example: "212-555-0100" },
  { key: "COMPANY_DIRECT_PHONE", required: false, description: "Company direct line", example: "212-555-0101" },
  { key: "COMPANY_MOBILE", required: false, description: "Mobile number", example: "917-555-0100" },
  { key: "TYPE", required: true, description: "GALLERY, COLLECTOR, ADVISOR, EXHIBITION, SHIPPER, ARTIST, PRODUCER, INTERIOR_DESIGNER, OTHER", example: "GALLERY" },
  { key: "RATE_TYPE", required: false, description: "STANDARD, STORAGE_CLIENT, VIP, VENDOR, CUSTOM", example: "STANDARD" },
  { key: "NOTE", required: false, description: "General notes about the client", example: "Preferred client" },
  { key: "ADDRESS", required: false, description: "Billing street address — if blank, uses first shipping address", example: "980 Madison Ave" },
  { key: "CITY", required: false, description: "Billing city — if blank, uses first shipping city", example: "New York" },
  { key: "STATE", required: false, description: "Billing state — if blank, uses first shipping state", example: "NY" },
  { key: "ZIP", required: false, description: "Billing zip — if blank, uses first shipping zip", example: "10075" },
  { key: "COUNTRY", required: false, description: "Billing country — if blank, uses first shipping country", example: "US" },
  { key: "SHIPPING_ADDRESS", required: true, description: "Shipping address(es), semicolon-separated for multiple", example: "100 Gallery Way; 200 Warehouse Rd" },
  { key: "SHIPPING_CITY", required: true, description: "Shipping city/cities, semicolon-separated", example: "New York; Brooklyn" },
  { key: "SHIPPING_STATE", required: true, description: "Shipping state(s), semicolon-separated", example: "NY; NY" },
  { key: "SHIPPING_ZIP", required: true, description: "Shipping zip(s), semicolon-separated", example: "10001; 11201" },
  { key: "SHIPPING_COUNTRY", required: false, description: "Shipping country/countries, semicolon-separated", example: "US; US" },
];

const AI_PROMPT = `I need you to convert my client/contact data into a CSV file with these exact column headers. Output ONLY the CSV content, nothing else.

REQUIRED COLUMNS (in this exact order):
COMPANY_NAME, FIRST_NAME, LAST_NAME, EMAIL, CONTACT_PHONE, COMPANY_DIRECT_PHONE, COMPANY_MOBILE, TYPE, RATE_TYPE, NOTE, ADDRESS, CITY, STATE, ZIP, COUNTRY, SHIPPING_ADDRESS, SHIPPING_CITY, SHIPPING_STATE, SHIPPING_ZIP, SHIPPING_COUNTRY

RULES:
- REQUIRED fields: COMPANY_NAME, FIRST_NAME, LAST_NAME, TYPE, SHIPPING_ADDRESS, SHIPPING_CITY, SHIPPING_STATE, SHIPPING_ZIP.
- At least one of EMAIL or CONTACT_PHONE must be provided per row.
- EMAIL: if not available but CONTACT_PHONE is provided, leave EMAIL blank (system will auto-generate).
- TYPE must be one of: GALLERY, COLLECTOR, ADVISOR, EXHIBITION, SHIPPER, ARTIST, PRODUCER, INTERIOR_DESIGNER, OTHER
- RATE_TYPE must be one of: STANDARD, STORAGE_CLIENT, VIP, VENDOR, CUSTOM
- For multiple shipping addresses, separate values with semicolons (;). Example: "100 Gallery Way; 200 Warehouse Rd"
- Use the same semicolon pattern for SHIPPING_CITY, SHIPPING_STATE, SHIPPING_ZIP, SHIPPING_COUNTRY so they align positionally.
- If billing address fields (ADDRESS, CITY, STATE, ZIP) are left blank, the system will auto-populate them from the first shipping address.
- Wrap any field containing commas in double quotes.
- Do not add any extra columns.

Here is my data to convert:
[PASTE YOUR DATA HERE]`;

const tableColumns = [
  {
    title: "Column Header",
    dataIndex: "key",
    key: "key",
    render: (text: string, record: (typeof CSV_COLUMNS)[0]) => (
      <Text strong={record.required} code>{text}</Text>
    ),
  },
  {
    title: "Required",
    dataIndex: "required",
    key: "required",
    width: 100,
    render: (val: boolean | string) =>
      val === "conditional" ? <Text className="text-amber-600">At least 1</Text> :
      val ? <Text type="danger">Yes</Text> : <Text type="secondary">No</Text>,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
  },
  {
    title: "Example",
    dataIndex: "example",
    key: "example",
    render: (text: string) => <Text type="secondary">{text}</Text>,
  },
];

export const ImportClientModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { filters } = useClientStore();
  const importClientsMutation = useImportClients();

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT);
      setCopied(true);
      message.success("AI prompt copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error("Failed to copy. Please try manually.");
    }
  };

  const handleUpload: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
  }) => {
    if (!(file instanceof File) || file.type !== "text/csv") {
      notification.error({
        message: "Invalid file type",
        description: "Please upload a valid CSV file.",
      });
      onError?.(new Error("Invalid file type"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notification.error({
        message: "File too large",
        description: "The CSV file must be smaller than 10MB.",
      });
      onError?.(new Error("File too large"));
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    importClientsMutation.mutate(formData, {
      onSuccess: (response) => {
        const { total, created, failed, errors } = response;
        if (created > 0) {
          notification.success({
            message: "Clients imported successfully!",
            description: `Successfully imported ${created} of ${total} clients.`,
          });
          queryClient.invalidateQueries({
            queryKey: CLIENT_KEYS.GET_CLIENTS(filters),
          });
          queryClient.invalidateQueries({
            queryKey: CLIENT_KEYS.GET_INFINITE_CLIENTS({}),
          });
          queryClient.invalidateQueries({
            queryKey: ["clientManagement/clients"],
          });
          setOpen(false);
        }
        if (failed > 0) {
          notification.warning({
            message: "Some clients failed to import",
            description: `${failed} clients could not be imported. Check the console for details.`,
          });
          console.error("Import errors:", errors);
        }
        onSuccess?.({});
      },
      onError: (err: any) => {
        notification.error({
          message: "Error importing clients",
          description:
            err.message || "There was an error processing the CSV file.",
        });
        onError?.(err);
      },
      onSettled: () => {
        setIsUploading(false);
      },
    });
  };

  return (
    <>
      <Button
        size="large"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm flex items-center space-x-2"
        onClick={() => setOpen(true)}
        disabled={isUploading}
      >
        <UploadOutlined />
        <span>Import Client CSV</span>
      </Button>
      <Modal
        open={open}
        title="Import Clients from CSV"
        onCancel={() => setOpen(false)}
        footer={null}
        width={900}
      >
        <Spin spinning={isUploading}>
          <div className="space-y-4">
            {/* AI Prompt Helper */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <Title level={5} className="!mt-0 !mb-1">
                Have client data in another format?
              </Title>
              <Text type="secondary" className="text-xs">
                Copy this prompt, paste it into ChatGPT or any AI tool along with your data (spreadsheet, PDF, list, etc.),
                and it will convert it into the exact CSV format needed.
              </Text>
              <div className="mt-3">
                <Button
                  icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyPrompt}
                  className={copied ? "border-green-500 text-green-600" : "border-gray-300 text-gray-700 hover:border-gray-400"}
                >
                  {copied ? "Copied!" : "Copy AI Conversion Prompt"}
                </Button>
              </div>
            </div>

            <Divider className="!my-3" />

            {/* Column Reference */}
            <div>
              <Title level={5} className="!mt-0 !mb-2">CSV Column Reference</Title>
              <Text type="secondary" className="block mb-3">
                Your CSV must use these exact column headers. Fields marked required must be filled for every row.
              </Text>
              <Table
                columns={tableColumns}
                dataSource={CSV_COLUMNS}
                rowKey="key"
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
              />
            </div>

            <Divider className="!my-3" />

            {/* Upload Section */}
            <div className="flex items-center gap-4">
              <Upload
                accept=".csv"
                customRequest={handleUpload}
                showUploadList={false}
                disabled={isUploading}
              >
                <Button type="primary" icon={<UploadOutlined />} size="large" disabled={isUploading}>
                  Upload CSV File
                </Button>
              </Upload>
              <Text type="secondary">Maximum file size: 10MB</Text>
            </div>
          </div>
        </Spin>
      </Modal>
    </>
  );
};
